import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/portfolio-capacity-allocation/
// portfolio-capacity-allocation.data-lake-source.ts
// (PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KIND_BY_EVIDENCE_KEY,
// PORTFOLIO_CAPACITY_ALLOCATION_DATA_LAKE_SCHEMA_VERSION). Confirmed live on
// tenant helix_retail 2026-08-06 (docker exec helix_postgres psql, read-only):
// all five contextKinds carry APPROVED, ingest-anchored AskHelixMemoryArtifact
// rows (sourceClass connector-derived, latest approvedAt 2026-07-16).
export const PORTFOLIO_CAPACITY_ALLOCATION_EVIDENCE_KEYS = {
  portfolioPlanSnapshot: 'portfolio_plan_snapshot',
  capacityActualsSnapshot: 'capacity_actuals_snapshot',
  dependencyConstraintSnapshot: 'dependency_constraint_snapshot',
  allocationPolicy: 'allocation_policy',
  financeActualsSnapshot: 'finance_actuals_snapshot',
} as const;

type PortfolioCapacityAllocationEvidenceKey =
  (typeof PORTFOLIO_CAPACITY_ALLOCATION_EVIDENCE_KEYS)[keyof typeof PORTFOLIO_CAPACITY_ALLOCATION_EVIDENCE_KEYS];

export const PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KIND_BY_EVIDENCE_KEY: Readonly<
  Record<PortfolioCapacityAllocationEvidenceKey, string>
> = {
  portfolio_plan_snapshot:
    'decision_agent:portfolio_capacity_allocation:portfolio_plan_snapshot',
  capacity_actuals_snapshot:
    'decision_agent:portfolio_capacity_allocation:capacity_actuals_snapshot',
  dependency_constraint_snapshot:
    'decision_agent:portfolio_capacity_allocation:dependency_constraint_snapshot',
  allocation_policy:
    'decision_agent:portfolio_capacity_allocation:allocation_policy',
  finance_actuals_snapshot:
    'decision_agent:portfolio_capacity_allocation:finance_actuals_snapshot',
};

export const PORTFOLIO_CAPACITY_ALLOCATION_SCHEMA_VERSION =
  'portfolio_capacity_allocation_governed_snapshot_v1';

const PORTFOLIO_CAPACITY_ALLOCATION_DOMAIN_BY_EVIDENCE_KEY: Readonly<
  Record<PortfolioCapacityAllocationEvidenceKey, 'strategy' | 'people' | 'operations' | 'finance'>
> = {
  portfolio_plan_snapshot: 'strategy',
  capacity_actuals_snapshot: 'people',
  dependency_constraint_snapshot: 'operations',
  allocation_policy: 'strategy',
  finance_actuals_snapshot: 'finance',
};

// Mirrors the module's own requiredState declarations (portfolio-capacity-allocation.manifest.ts):
// the four planning-scope keys serve planning too; finance_actuals_snapshot does not.
const PORTFOLIO_CAPACITY_ALLOCATION_REQUIRED_FOR_BY_EVIDENCE_KEY: Readonly<
  Record<
    PortfolioCapacityAllocationEvidenceKey,
    readonly ('planning' | 'monitoring' | 'scenario' | 'decision_pack' | 'activation' | 'ask_helix')[]
  >
> = {
  portfolio_plan_snapshot: ['planning', 'monitoring', 'scenario', 'decision_pack', 'activation', 'ask_helix'],
  capacity_actuals_snapshot: ['planning', 'monitoring', 'scenario', 'decision_pack', 'activation', 'ask_helix'],
  dependency_constraint_snapshot: [
    'planning',
    'monitoring',
    'scenario',
    'decision_pack',
    'activation',
    'ask_helix',
  ],
  allocation_policy: ['planning', 'monitoring', 'scenario', 'decision_pack', 'activation', 'ask_helix'],
  finance_actuals_snapshot: ['monitoring', 'scenario', 'decision_pack', 'activation', 'ask_helix'],
};

const NO_PORTFOLIO_CAPACITY_ALLOCATION_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

function portfolioCapacityAllocationEntry(evidenceKey: PortfolioCapacityAllocationEvidenceKey) {
  return defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KIND_BY_EVIDENCE_KEY[evidenceKey],
    schemaVersion: PORTFOLIO_CAPACITY_ALLOCATION_SCHEMA_VERSION,
    bindingPlaceholders: NO_PORTFOLIO_CAPACITY_ALLOCATION_BINDING_PLACEHOLDERS,
    domain: PORTFOLIO_CAPACITY_ALLOCATION_DOMAIN_BY_EVIDENCE_KEY[evidenceKey],
    freshnessCadence: 'weekly',
    requiredFor: [...PORTFOLIO_CAPACITY_ALLOCATION_REQUIRED_FOR_BY_EVIDENCE_KEY[evidenceKey]],
    missingBehavior: 'block',
    description:
      `Approved portfolio-capacity-allocation ${evidenceKey} evidence published by the governed ` +
      'data-lake source; one of the five artifacts the kernel evidence pack must resolve, sharing ' +
      'tenant, evidence-pack ref, strategy, portfolio scope, and reporting cut before use.',
  });
}

export const PORTFOLIO_CAPACITY_ALLOCATION_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'portfolio-capacity-allocation',
  {
    portfolio_plan_snapshot: portfolioCapacityAllocationEntry('portfolio_plan_snapshot'),
    capacity_actuals_snapshot: portfolioCapacityAllocationEntry('capacity_actuals_snapshot'),
    dependency_constraint_snapshot: portfolioCapacityAllocationEntry('dependency_constraint_snapshot'),
    allocation_policy: portfolioCapacityAllocationEntry('allocation_policy'),
    finance_actuals_snapshot: portfolioCapacityAllocationEntry('finance_actuals_snapshot'),
  },
  {
    evidenceKeys: Object.values(PORTFOLIO_CAPACITY_ALLOCATION_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
    contextKinds: Object.values(PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KIND_BY_EVIDENCE_KEY),
    schemaVersions: [PORTFOLIO_CAPACITY_ALLOCATION_SCHEMA_VERSION],
    stateKeys: Object.values(PORTFOLIO_CAPACITY_ALLOCATION_EVIDENCE_KEYS),
    bindingPlaceholders: NO_PORTFOLIO_CAPACITY_ALLOCATION_BINDING_PLACEHOLDERS,
  },
);

export type PortfolioCapacityAllocationModuleEvidenceContract =
  typeof PORTFOLIO_CAPACITY_ALLOCATION_MODULE_EVIDENCE_CONTRACT;
